/* eslint no-param-reassign: 'off', no-console: 'off' */
import Vuex from 'vuex';
import download from 'downloadjs';

const terminalsStore = new Vuex.Store({
  state: {
    terminals: [],
    terminalsTotal: 0,
    terminalsLoading: false,
    terminalsLoadingError: null,
    terminal: null,
    terminalLoading: false,
    terminalLoadingError: null,
    hubState: null,
    hubStateLoading: false,
    hubStateLoadingError: null,
    usage: null,
    usageLoading: false,
    usageLoadingError: null,
    allSoftwareVersions: [],
    terminalSpecificSoftwareVersions: [],
    metricProviders: [],
    metricTypes: [],
    terminalMetrics: [],
    terminalsForSubscription: [],
  },
  mutations: {
    fetchTerminals(state, payload) {
      state.terminals = payload.items;
      state.terminalsTotal = payload.count;
    },
    fetchTerminal(state, payload) {
      state.terminal = payload;
    },
    fetchTerminalHubState(state, payload) {
      state.hubState = payload;
    },
    deleteTerminal(state, deletedTerminalId) {
      state.terminals.items = state.terminals.items.filter(
        (t) => t.id != deletedTerminalId
      );
      state.terminals.count--;
      state.terminal = null;
    },
    setFeatureCertificate(state, payload) {
      state.terminal.features = payload.features
        ? payload.features.split(',')
        : [];
      state.terminal.publicKey = payload.publicKey;
    },
    fetchSoftwareVersions(state, payload) {
      state.allSoftwareVersions = payload;
    },
    fetchSoftwareVersionsForTerminal(state, payload) {
      state.terminalSpecificSoftwareVersions = payload;
    },
    fetchMetricProviders(state, providers) {
      state.metricProviders = providers;
    },
    fetchMetricTypes(state, payload) {
      state.metricTypes = payload;
    },
    fetchMetrics(state, payload) {
      state.terminalMetrics = payload;
    },
    fetchTerminalsForSubscription(state, payload) {
      state.terminalsForSubscription = payload.items;
    },
    fetchTerminalUsage(state, payload) {
      state.usage = payload;
    }
  },
  actions: {
    async fetchTerminals({ state, commit }, payload) {
      try {
        state.terminalsLoading = true;
        const res = await fetch(
          `/v2/terminals?skip=${payload.skip}&take=${payload.take}&sortField=${
            payload.sortField || ''
          }&sortDirection=${payload.sortDirection || ''}&query=${
            payload.query || ''
          }&fields=${payload.fields || ''}&filters=${payload.filters || ''}`
        );
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!payload.skipCommit) commit('fetchTerminals', data);
        return data;
      } catch (ex) {
        state.terminalsLoadingError = ex;
        return { isError: true, ex };
      } finally {
        state.terminalsLoading = false;
      }
    },
    async fetchTerminal({ state, commit }, payload) {
      if (!payload) return null;
      try {
        state.terminalLoading = true;
        const res = await fetch(`/v2/terminals/${payload.id}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        commit('fetchTerminal', data);
        return data;
      } catch (ex) {
        state.terminalLoadingError = ex;
        return { isError: true, ex };
      } finally {
        state.terminalLoading = false;
      }
    },
    async fetchTerminalHubState({ state, commit }) {
      if (!state.terminal?.id) return null;

      try {
        state.hubStateLoading = true;
        const res = await fetch(`/v2/terminals/${state.terminal?.id}/hubState`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        // const data = sampleHubState; // TODO: remove this later
        commit('fetchTerminalHubState', data);
        return data;
      } catch (ex) {
        state.hubStateLoadingError = ex;
        return { isError: true, ex };
      } finally {
        state.hubStateLoading = false;
      }
    },
    async deleteTerminal({ state, commit }, id) {
      try {
        const res = await fetch(`/v2/terminals/${id ?? state.terminal?.id}`, {
          method: 'DELETE',
        });
        const d = await res.text();
        if (!res.ok) throw new Error(d);
        commit('deleteTerminal', state.terminal?.id);
        return res.ok;
      } catch (ex) {
        state.hubStateLoadingError = ex;
        return { isError: true, ex };
      } finally {
        state.hubStateLoading = false;
      }
    },
    async updateTerminal({ state, commit }, payload) {
      try {
        const res = await fetch(
          `/v2/terminals/${state.terminal.id}`,
          {
            body: JSON.stringify(payload),
            method: 'PUT',
            headers: new Headers({ 'Content-Type': 'application/json' }),
          }
        );
        if (!res.ok) return { isSuccess: false, data: await res.text() };
        const data = await res.json();
        commit('fetchTerminal', data);
        return { isSuccess: true, data };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, data: ex.message };
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async calculateDHCPFields({}, payload) {
      try {
        const res = await fetch('/v2/terminals/networkconfig/dhcp', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' }),
        });
        if (!res.ok) return { isSuccess: false, data: await res.text() };
        const data = await res.json();
        return { isSuccess: true, data };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, data: ex.message };
      }
    },
    async updateNetworkConfig({ state, commit }, payload) {
      try {
        const res = await fetch(
          `/v2/terminals/${state.terminal.id}/networkConfig`,
          {
            body: JSON.stringify(payload),
            method: 'PUT',
            headers: new Headers({ 'Content-Type': 'application/json' }),
          }
        );
        if (!res.ok) return { isSuccess: false, data: await res.text() };
        const data = await res.json();
        commit('fetchTerminal', data);
        return { isSuccess: true, data };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, data: ex.message };
      }
    },
    async updateHardwareModels({ state, commit }, payload) {
      try {
        const res = await fetch(
          `/v2/terminals/${state.terminal.id}/hardwareModels`,
          {
            body: JSON.stringify(payload),
            method: 'PUT',
            headers: new Headers({ 'Content-Type': 'application/json' }),
          }
        );
        if (!res.ok) return { isSuccess: false, data: await res.text() };
        const data = await res.json();
        commit('fetchTerminal', data);
        return { isSuccess: true, data };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, data: ex.message };
      }
    },
    async generateFeaturesCertificate({ state, commit }, payload) {
      try {
        const res = await fetch(`/v2/terminals/${state.terminal.id}/features`, {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' }),
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!data) return false;
        commit('setFeatureCertificate', data);
        return data;
      } catch (err) {
        console.error(err);
        return false;
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async downloadOptionsFile({}, payload) {
      try {
        const res = await fetch(
          `/v2/terminals/${payload.id}/downloadOptionsFiles`
        );
        if (!res.ok) return { isSuccess: false, error: await res.text() };
        const blob = await res.blob();
        const fileName = `${
          payload?.hardware?.geoModem?.serial || payload?.name
        }-optionsFile.zip`;
        download(blob, fileName, 'application/zip');
        return { isSuccess: res.ok };
      } catch (ex) {
        console.error(ex);
        return { isSucess: false, error: ex };
      }
    },
    async executeTerminalCommand({ commit }, payload) {
      try {
        const res = await fetch(
          `/v2/terminals/${payload.id}/command/${payload.cmd}`,
          {
            body: JSON.stringify(payload.data),
            method: 'POST',
            headers: new Headers({ 'Content-Type': 'application/json' }),
          }
        );
        if (!res.ok) throw new Error(`Error executing command ${payload.cmd} on terminal ${payload.id}: ${await res.text()}`);
        const terminal = await res.json();
        commit('fetchTerminal', terminal);
        // return response result
        return res.ok;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchSoftwareVersions({ commit }, payload) {
      try {
        const res = await fetch(
          `/v3/info/softwareVersions?currentVersion=${payload}`
        );
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!payload) {
          commit('fetchSoftwareVersions', data);
        } else {
          commit('fetchSoftwareVersionsForTerminal', data);
        }
        return data;
      } catch (ex) {
        return { isError: true, ex };
      }
    },
    async fetchMetricProviders({ commit }) {
      try {
        const res = await fetch('/v2/info/metricProviders');
        if (!res.ok) throw new Error(await res.text());
        const providers = await res.json();
        commit('fetchMetricProviders', providers);
        return providers;
      } catch (error) {
        console.error('Unable to fetch metric providers: ', error);
        return false;
      }
    },
    async fetchMetricTypes({ commit }) {
      try {
        const res = await fetch('/v2/info/metrics');
        if (!res.ok) throw new Error(await res.text());
        const metrics = await res.json();
        commit('fetchMetricTypes', metrics);
        return metrics;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchMetrics({ commit }, payload) {
      try {
        const res = await fetch('/v2/terminals/metrics', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' }),
        });
        const response = await res.json();
        commit('fetchMetrics', response);
        return true;
      } catch (ex) {
        console.error('[fetchTerminalMetrics] Error: ', ex);
        return false;
      }
    },
    async fetchTerminalsForSubscription({ commit }, payload) {
      try {
        const res = await fetch(
          `/v2/terminals?skip=0&take=20&sortField=Name&sortDirection=ASC&query=&fields=Id,Name,Serial&filters=ActiveSubscription_SubscriptionId:${payload.id}`
        );
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        commit('fetchTerminalsForSubscription', data);
        return data;
      } catch (ex) {
        return { isError: true, ex };
      }
    },
    async fetchTerminalUsage({ state, commit }, payload) {
      if (!state.terminal?.id) return null;

      try {
        state.usageLoading = true;
        const res = await fetch(`/v2/terminals/${state.terminal?.id}/usage?year=${payload.year}&month=${payload.month}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        commit('fetchTerminalUsage', data);
        return data;
      } catch (ex) {
        state.usageLoadingError = ex;
        return { isError: true, ex };
      } finally {
        state.usageLoading = false;
      }
    }
  },
  getters: {},
});

export default terminalsStore;
export const useTerminalsStore = () => terminalsStore;
